import { useQueryGetAllBots } from "@apis:bot/bot";
import { useQueryGetAllUiPreferences } from "@apis:ui_preferences/ui_preferences";
import { useQueryGetAllUsers } from "@apis:user/user";
import { JsonTableRenderer } from "@components:JsonTableRenderer";
import { SuperDataGrid } from "@components:SuperDataGrid";
import { usePaginationSortDataGrid } from "@hooks:usePaginationSortDataGrid";
import { useTableFilter } from "@hooks:useTableFilter";
import { Typography, useTheme } from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import { useUiPreferencesData } from "@stores:settings/ui-preferences";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdDelete, MdModeEdit } from "react-icons/md";

import { UiPreferenceDeleteModal } from "./UiPreferenceDeleteModal";
import { UiPreferenceModal } from "./UiPreferenceModal";

export const UiPreferencesTable = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isOpenModalToUpdate, setIsOpenModalToUpdate] = useState(false);
  const [isOpenModalToDelete, setIsOpenModalToDelete] = useState(false);
  const [selectedUiPreferenceId, setSelectedUiPreferenceId] = useState<
    string | null
  >(null);
  const { searchKeyword, state } = useUiPreferencesData();
  const { paginationProps, sortProps } = usePaginationSortDataGrid({
    pageSize: 30,
    defaultSortModel: {
      field: "userId",
      sort: "asc",
    },
  });

  const { data: uiPreferences, isLoading: isLoadingPreferences } =
    useQueryGetAllUiPreferences(state === "active");
  const { data: users, isLoading: isLoadingUsers } = useQueryGetAllUsers();
  const { data: bots, isLoading: isLoadingBots } = useQueryGetAllBots(false);

  const isLoading = isLoadingPreferences || isLoadingUsers || isLoadingBots;

  const enrichedData = useMemo(() => {
    if (!uiPreferences || !users) return [];

    return uiPreferences.map((preference) => {
      const user = users.find((u) => u.id === preference.userId);
      const bot = preference.botId
        ? bots?.find((b) => b.id === preference.botId)
        : null;

      return {
        ...preference,
        user: user
          ? {
              id: user.id,
              email: user.email,
              name: user.name,
            }
          : null,
        bot: bot
          ? {
              id: bot.id,
              name: bot.name,
            }
          : null,
        preferenceType: preference.botId ? "Bot Preference" : "User Preference",
      };
    });
  }, [uiPreferences, users, bots]);

  const cleanedData = useTableFilter({
    filters: [
      {
        field: "enabled",
        operator: "equals",
        value: state === "active",
      },
      {
        OR: [
          {
            field: "user.email",
            operator: "contains",
            value: searchKeyword,
          },
          {
            field: "user.name",
            operator: "contains",
            value: searchKeyword,
          },
          {
            field: "bot.name",
            operator: "contains",
            value: searchKeyword,
          },
          {
            field: "id",
            operator: "contains",
            value: searchKeyword,
          },
        ],
      },
    ],
    data: enrichedData,
  });

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "preferenceType",
        headerName: t("preferences.uiPreferences.columnType"),
        width: 150,
        sortable: false,
        renderCell: (params) => {
          const isBotPreference = params.value === "Bot Preference";
          return (
            <Typography
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                fontWeight: 500,
                color: isBotPreference ? "primary.main" : "secondary.main",
              }}
            >
              {t(
                isBotPreference
                  ? "preferences.uiPreferences.botPreference"
                  : "preferences.uiPreferences.userPreference",
              )}
            </Typography>
          );
        },
      },
      {
        field: "user",
        headerName: t("preferences.uiPreferences.user"),
        width: 200,
        sortable: false,
        renderCell: (params) => {
          const user = params.value;
          if (!user) {
            return (
              <Typography color="text.secondary">
                {t("preferences.uiPreferences.userNotFound", {
                  id: params.row.userId,
                })}
              </Typography>
            );
          }
          return (
            <Typography
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
              }}
            >
              {user.name}
            </Typography>
          );
        },
      },
      {
        field: "bot",
        headerName: t("preferences.uiPreferences.bot"),
        width: 200,
        sortable: false,
        renderCell: (params) => {
          const bot = params.value;
          if (!bot && params.row.botId) {
            return (
              <Typography color="text.secondary">
                {t("preferences.uiPreferences.botNotFound", {
                  id: params.row.botId,
                })}
              </Typography>
            );
          }
          if (!bot) {
            return (
              <Typography
                color="text.secondary"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                {t("preferences.notApplicable")}
              </Typography>
            );
          }
          return (
            <Typography
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
              }}
            >
              {bot.name}
            </Typography>
          );
        },
      },
      {
        field: "preference",
        headerName: t("preferences.uiPreferences.columnPreference"),
        width: 300,
        sortable: false,
        renderCell: (params) => {
          const preference = params.value;
          return <JsonTableRenderer data={preference} />;
        },
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        flex: 1,
        align: "right",
        getActions: (params) => {
          const editButton = (
            <GridActionsCellItem
              key="edit"
              icon={<MdModeEdit size={20} />}
              label={t("common.edit")}
              className="textPrimary"
              color="primary"
              onClick={() => {
                setIsOpenModalToUpdate(true);
                setSelectedUiPreferenceId(params.row.id);
              }}
            />
          );

          const deleteButton = (
            <GridActionsCellItem
              key="delete"
              icon={<MdDelete size={20} />}
              label={t("common.delete")}
              style={{ color: theme.palette.error.main }}
              onClick={async () => {
                setIsOpenModalToDelete(true);
                setSelectedUiPreferenceId(params.row.id);
              }}
            />
          );

          const actions = [editButton];
          if (params?.row?.enabled) {
            actions.push(deleteButton);
          }

          return actions;
        },
      },
    ],
    [t, theme.palette.error.main],
  );

  return (
    <>
      <SuperDataGrid
        rows={cleanedData}
        columns={columns}
        loading={isLoading}
        {...paginationProps}
        {...sortProps}
        getRowId={(row) => row.id}
        for="ui-preferences"
        noRowsOverlayText={t("preferences.uiPreferences.noneFound")}
        sx={{
          "& .MuiDataGrid-row--borderBottom": {
            backgroundColor: "white !important",
          },
        }}
      />
      {isOpenModalToUpdate && selectedUiPreferenceId && (
        <UiPreferenceModal
          isOpen={isOpenModalToUpdate}
          onClose={() => {
            setIsOpenModalToUpdate(false);
            setSelectedUiPreferenceId(null);
          }}
          uiPreferenceId={selectedUiPreferenceId}
        />
      )}
      {isOpenModalToDelete && selectedUiPreferenceId && (
        <UiPreferenceDeleteModal
          isOpen={isOpenModalToDelete}
          onClose={() => {
            setIsOpenModalToDelete(false);
            setSelectedUiPreferenceId(null);
          }}
          uiPreferenceId={selectedUiPreferenceId}
        />
      )}
    </>
  );
};
